import { AnalyticsEventMap, AnalyticsEventType, DefaultData } from './events';
import { mapConfigToDriver } from './helpers/mapConfigToDriver';
import { AnalyticsConfig, Driver, DriverConfig } from './types';

/**
 * Analytics
 *
 * Package used to manage sending analytics events to multiple services
 *
 * @param config - Configuration of which services will be delivered analytic events
 * @returns An object of methods that can be used to log analytic events
 */
export const Analytics = (config: AnalyticsConfig) => {
    /**
     * Holds the state needed for the package to operate
     */
    const state: {
        defaultData: DefaultData | undefined;
        drivers: Driver[];
    } = {
        defaultData: undefined,
        drivers: config.drivers.map((driverConfig) =>
            mapConfigToDriver(driverConfig),
        ),
    };

    /**
     * addDriver
     *
     * Adds a analytics driver to the set up after the package is called
     *
     * @param driverConfig - Config object for the driver
     */
    const addDriver = (driverConfig: DriverConfig): void => {
        state.drivers.push(mapConfigToDriver(driverConfig));
    };

    /**
     * initialize
     *
     * Performs any tasks needed to set up the specific analytics driver
     */
    const initialize = (): Promise<void[]> =>
        Promise.all(state.drivers.map((driver) => driver.initialize()));

    /**
     * trigger
     *
     * Trigger an analytics event to fire on all drivers
     *
     * @param payload - Payload of the analytics event
     */
    const trigger = <Type extends AnalyticsEventType>(
        payload: AnalyticsEventMap[Type],
    ): void => {
        const { defaultData } = state;

        if (typeof defaultData === 'undefined')
            // eslint-disable-next-line no-console
            console.warn('Default data not set!');
        else
            state.drivers.forEach((driver) =>
                driver.trigger(payload, defaultData),
            );
    };

    /**
     * enable
     *
     * Enable analytic events to be sent if they were previously disabled
     */
    const enable = (): void => {
        state.drivers.forEach((driver) => driver.enable());
    };

    /**
     * disable
     *
     * Disable analytic events to be sent if they were previously enabled
     */
    const disable = (): void => {
        state.drivers.forEach((driver) => driver.disable());
    };

    /**
     * setDefaultData
     *
     * Initially sets the default data that is needed to send any analytic events
     *
     * @param data - An object containing all the properties in default data to be set
     */
    const setDefaultData = (data: DefaultData): void => {
        state.defaultData = data;

        state.drivers.forEach((driver) => driver.setDefaultData(data));
    };

    /**
     * updateDefaultData
     *
     * Updates the existing default data that is needed to send any analytic events
     *
     * @param data - An object containing the properties in default data to update
     */
    const updateDefaultData = (data: Partial<DefaultData>): void => {
        if (!state.defaultData) {
            // eslint-disable-next-line no-console
            console.warn('Default data not set!');
            return;
        }

        const updatedDefaultData = {
            ...state.defaultData,
            ...data,
        };

        state.defaultData = updatedDefaultData;

        state.drivers.forEach((driver) =>
            driver.setDefaultData(updatedDefaultData),
        );
    };

    return {
        addDriver,
        disable,
        enable,
        initialize,
        trigger,
        setDefaultData,
        updateDefaultData,
    };
};
export type Analytics = ReturnType<typeof Analytics>;
